import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import { DateTimePicker } from "../../components/ui/DateTimePicker";
import { Button } from "../../components/ui/button";
import { DiscardConfirmDialog, useDiscardGuard } from "../../components/DiscardConfirmDialog";
export type AccessAction = "review" | "revoke";
/* Both actions ask the same two questions, so one dialog covers them and the
   wording/tone comes off the action. */
const COPY: Record<
  AccessAction,
  { title: string; placeholder: string; submit: string; tone: string }
> = {
  review: {
    title: "Review access",
    placeholder: "Why is this access review required?",
    submit: "Submit review",
    tone: "bg-indigo-600 hover:bg-indigo-500",
  },
  revoke: {
    title: "Revoke access",
    placeholder: "Why should this access be revoked?",
    submit: "Submit revoke",
    tone: "bg-red-600 hover:bg-red-500",
  },
};
export interface AccessActionPayload {
  justification: string;
  dueDate: string;
}
export const AccessActionDialog = ({
  action,
  selectedCount,
  submitting = false,
  onClose,
  onSubmit,
}: {
  /** null keeps the dialog closed. */
  action: AccessAction | null;
  selectedCount: number;
  submitting?: boolean;
  onClose: () => void;
  onSubmit: (action: AccessAction, payload: AccessActionPayload) => void;
}) => {
  const [justification, setJustification] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [submitted, setSubmitted] = useState(false);
  /* Each opening starts clean, so one request's wording never carries into
     the next. */
  useEffect(() => {
    if (!action) return;
    setJustification("");
    setDueDate("");
    setSubmitted(false);
  }, [action]);
  const isDirty = Boolean(justification.trim() || dueDate.trim());
  const { requestClose, confirmProps } = useDiscardGuard(isDirty, onClose);
  if (!action) return null;
  const copy = COPY[action];
  const justificationMissing = !justification.trim();
  const dueDateMissing = !dueDate.trim();
  const submit = () => {
    setSubmitted(true);
    if (justificationMissing || dueDateMissing) return;
    onSubmit(action, { justification: justification.trim(), dueDate });
  };
  return (
    <>
      <Dialog
        open
        onOpenChange={(next) => {
          if (!next && !submitting) requestClose();
        }}
      >
        <DialogContent
          /* overflow-visible: the date picker's calendar pops outside the
             dialog box and would otherwise be cut off. */
          className="gap-0 overflow-visible p-0 sm:max-w-md"
          onOpenAutoFocus={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => {
            /* Allow pointer events on the DateTimePicker calendar portal
               (portaled outside the Dialog DOM) to reach the day buttons. */
            const target = e.target as HTMLElement | null;
            if (target?.closest?.('[data-part="popover"]')) {
              e.preventDefault();
            }
          }}
        >
          <DialogHeader className="gap-1 border-b border-border p-5 pr-12 text-left">
            <DialogTitle className="text-base font-semibold">{copy.title}</DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              {selectedCount} selected {selectedCount === 1 ? "account" : "accounts"}
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-4 p-5">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="access-action-justification" className="text-sm font-medium text-foreground">
                Justification <span className="text-red-500">*</span>
              </label>
              <Textarea
                id="access-action-justification"
                rows={3}
                maxLength={500}
                value={justification}
                onChange={(e) => {
                  setJustification(e.target.value);
                }}
                placeholder={copy.placeholder}
                className={`resize-none border border-border bg-background ${submitted && justificationMissing ? "!border-red-500 focus-visible:ring-red-500" : ""}`}
              />
              {submitted && justificationMissing && (
                <span className="text-[11px] text-red-500">Justification is required</span>
              )}
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="access-action-due-date" className="text-sm font-medium text-foreground">
                Due date <span className="text-red-500">*</span>
              </label>
              <DateTimePicker
                id="access-action-due-date"
                variant="single"
                valueType="string"
                value={dueDate || undefined}
                onValueChange={(value) => {
                  setDueDate(value ?? "");
                }}
                minDate={new Date()}
                placeholder="DD/MM/YYYY"
                displayFormat="dd/MM/yyyy"
                clearable
                disabled={submitting}
                state={submitted && dueDateMissing ? "error" : "default"}
              />
              {submitted && dueDateMissing && (
                <span className="text-[11px] text-red-500">Due date is required</span>
              )}
            </div>
          </div>
          <DialogFooter className="flex-row justify-end gap-3 border-t border-border p-5">
            <Button variant="outline"
              type="button"
              onClick={requestClose}
              disabled={submitting}
              className="justify-start rounded-md px-5 text-sm font-medium text-foreground hover:border-indigo-400/60 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Cancel
            </Button>
            <Button variant="ghost"
              type="button"
              onClick={submit}
              disabled={submitting}
              className={`justify-start h-auto px-0 py-0 font-normal inline-flex h-9 items-center rounded-md px-5 text-sm font-semibold text-white transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${copy.tone}`}
            >
              {submitting ? "Submitting" : copy.submit}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <DiscardConfirmDialog {...confirmProps} />
    </>
  );
};
